import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

type DropDownProps = {
  options: string[];
  disabled?: boolean;
  bgColor?: string;
  borderColor?: string;
  fontColor?: string;
  fontSize?: string;
  hoverBgColor?: string;
  hoverFontColor?: string;
  hoverBorderColor?: string;
  width?: string;
  height?: string;
  radius?: number;
  onChange?: (value: string) => void;
};

const DropDown: React.FC<DropDownProps> = ({
  options,
  onChange,
  disabled = false,
  bgColor = 'bg-white',
  borderColor = 'border-BADBlue',
  fontColor = 'text-BADBlue',
  fontSize = 'text-base',
  hoverBgColor = 'bg-BAMPink',
  hoverFontColor = 'text-BAWhite',
  hoverBorderColor = 'border-BAMPink',
  width = 'w-[100%]',
  height = 'h-auto',
  radius = 6,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string>(options[0] || '');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectOption = (value: string) => {
    setSelectedValue(value);
    setIsOpen(false);
    onChange && onChange(value);
  };

  const borderClass = borderColor ? `border ${borderColor}` : '';
  const radiusClass = `rounded-[${radius}px]`;
  const buttonBgColor = isOpen ? bgColor : 'bg-transparent';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`flex items-center justify-between px-3 py-2 ${buttonBgColor} ${fontSize} ${fontColor} ${borderClass} ${width} ${height} font-normal ${radiusClass} disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors ${hoverBgColor ? `hover:${hoverBgColor}` : ''} ${hoverFontColor ? `hover:${hoverFontColor}` : ''} ${hoverBorderColor ? `hover:${hoverBorderColor}` : ''}`}
      >
        <span className="truncate">{selectedValue}</span>
        <ChevronDown 
          className={`ml-2 w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div
          className={`absolute top-full left-0 ${width} mt-1 bg-white ${borderClass} ${radiusClass} shadow-lg z-50 max-h-[300px] overflow-y-auto`}
        >
          {options.length > 0 ? (
            <div className="py-1">
              {options.map((val, index) => (
                <button
                  key={index}
                  onClick={() => handleSelectOption(val)}
                  className={`w-full text-left px-3 py-2 ${fontSize} ${fontColor} transition-colors ${hoverBgColor ? `hover:${hoverBgColor}` : ''} ${hoverFontColor ? `hover:${hoverFontColor}` : ''}`}
                >
                  {val}
                </button>
              ))}
            </div>
          ) : (
            <div className="px-3 py-2 text-sm text-center opacity-50">
              No options available
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DropDown;
